import { faExternalLink } from '@fortawesome/pro-solid-svg-icons/faExternalLink';
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons/faMapMarkerAlt';
import { School } from '@propertypal/shared/src/types/schools';
import React, { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import Chevron from '../icons/Chevron';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { LinkText, Text } from '../typography';
import InfoBox from './InfoBox';
import InfoIcon from './InfoIcon';
import { Container, LoadingBox, SchoolInfoRow, SchoolInfo, SchoolTitle } from './SchoolsTable.style';

interface Props {
  schools?: School[];
  isROI?: boolean;
  error?: Error;
  loading: boolean;
  errorText: string;
  distanceAppendix: string;
  onViewMore?: () => void;
  showViewMore?: boolean;
  schoolType: string;
}

interface InfoBoxParams {
  school: School;
  target: EventTarget;
}

const SchoolsTable: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const [activeInfoBox, setActiveInfoBox] = useState<InfoBoxParams | null>(null);
  const yearText = ['roiprimary', 'primary'].includes(props.schoolType) ? 'Y1' : 'Y8';

  const openInfoBox = (school: School) => (e: MouseEvent) => {
    e.stopPropagation();
    setActiveInfoBox({
      school,
      target: e.target,
    });
  };

  useEffect(() => {
    setActiveInfoBox(null);
  }, [props.schools]);

  if (props.loading) {
    return (
      <LoadingBox data-testid="schools-loading">
        <Loader color={theme.backgroundLight} />
      </LoadingBox>
    );
  }

  if (props.error || !props.schools) {
    return (
      <div style={{ padding: '0 15px' }}>
        <Text align="center" fontWeight="bold" mt={30} mb={30}>
          {props.errorText}
        </Text>
      </div>
    );
  }

  return (
    <Container className="pp-schools-table">
      {activeInfoBox && (
        <InfoBox
          school={activeInfoBox.school}
          onClose={() => setActiveInfoBox(null)}
          target={activeInfoBox.target as HTMLElement}
        />
      )}

      {props.schools.map((school) => (
        <React.Fragment key={school.name}>
          <SchoolTitle>
            {school.name.split(' ').map((word, index, words) => (
              <span key={word}>
                {word}
                {words.length - 1 === index ? (
                  <InfoIcon color={theme.primary} onClick={openInfoBox(school)} />
                ) : (
                  <>&nbsp;</>
                )}
              </span>
            ))}
          </SchoolTitle>

          <SchoolInfoRow>
            {school.distanceText && (
              <SchoolInfo $hasIcon className="pp-school-cell">
                <FontAwesomeIcon icon={faMapMarkerAlt} color={theme.textDark} size={13} /> {school.distanceText} from
                this {props.distanceAppendix}
              </SchoolInfo>
            )}

            {!props.isROI && (
              <>
                <SchoolInfo data-testid="numberOfAdmissions" $hasIcon={!school.distanceText} className="pp-school-cell">
                  <b>{school.years[0].numberOfAdmissions}</b> {yearText} Approved Admissions
                </SchoolInfo>

                <SchoolInfo $hasIcon={!school.distanceText} className="pp-school-cell">
                  <b>{school.years[0].totalApplications}</b> {yearText} Applications
                </SchoolInfo>

                <SchoolInfo $hasIcon={!school.distanceText} className="pp-school-cell">
                  <b>{school.years[0].totalAdmissions}</b> {yearText} Admissions
                </SchoolInfo>

                <SchoolInfo $hasIcon={!school.distanceText} className="pp-school-cell">
                  <b>{school.years[0].numberOfPupils}</b> Approved enrolment
                </SchoolInfo>

                <SchoolInfo $hasIcon={!school.distanceText} className="pp-school-cell">
                  <a href={school.years[0].admissionsPDF} target="_blank" rel="noreferrer">
                    Admissions criteria{' '}
                    <FontAwesomeIcon icon={faExternalLink} size={13} style={{ marginLeft: 2, marginTop: 1 }} />
                  </a>
                </SchoolInfo>
              </>
            )}

            {props.isROI && (
              <>
                <SchoolInfo data-testid="totalPupils" $hasIcon={!school.distanceText} className="pp-school-cell">
                  <b>{school.years[0].totalPupilsROI}</b> Total Pupils
                </SchoolInfo>

                <SchoolInfo $hasIcon={!school.distanceText} className="pp-school-cell">
                  {school.ethosText}
                </SchoolInfo>

                <SchoolInfo $hasIcon={!school.distanceText} className="pp-school-cell">
                  {school.genderText}
                </SchoolInfo>
              </>
            )}
          </SchoolInfoRow>
        </React.Fragment>
      ))}

      {props.showViewMore && props.onViewMore && (
        <LinkText
          as="button"
          aria-label="Show all schools in area"
          className="pp-schools-view-more"
          onClick={props.onViewMore}
          data-testid="schoolViewMoreButton"
          type="button"
          fontSize={14}
        >
          View More <Chevron size={9} />
        </LinkText>
      )}
    </Container>
  );
};

export default SchoolsTable;
